import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {timer} from "rxjs";

import {drawCurve} from '../../defines/curved_line.func';
import {INTONATION_STEP, RECORDER_CONFIG} from '../../defines/pitch.const';

// 파형 데이터 값은 0.0 ~ 1.0 사이.
// 이 중에서 30% 만 사용해서 표시하겠다는 의미
const MAX_DIFF = 0.3;
const MAX_NAN_DRAW_LINE = 4; // NaN이 이 숫자 이하인 경에는 선을 연결한다.
const MAKE_NARROW = 3;      // 벌어진 간격을 좁히기 위한 값.
const RATIO_PADDING_Y = 0.24;  // 노란 점선을 표시하는 위치 (높이의 몇% 위치에 그리는지 지정)

enum STATE {
  INIT,
  CHECK_NAN,  // NaN이 나왔는데 선을 이어야할지 말지 모를 때
  EMPTY_NAN,  // NaN이 길어져서 선이 끊어진 상태
  NOT_NAN,    // 숫자가 나온 상태.
}

@Component({
  selector: 'app-wav-graph',
  template: `
    <canvas #wavCanvas
            [attr.width]="offsetWidth"
            [attr.height]="offsetHeight">
    </canvas>`,
})
export class WavGraphComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('wavCanvas') wavCanvas?: ElementRef;
  @Input() data: Array<number> | null = null;
  @Input() recorder?: RECORDER_CONFIG;
  @Input() paddingX = 10;
  @Input() paddingY = 10;
  @Input() showLine = true;

  public offsetWidth?: number;
  public offsetHeight?: number;
  private startX?: number;
  private startY?: number;
  private ratioX?: number;
  private ratioY?: number;
  // private avg?: number;
  private maxDiff?: number;
  private lineDiff?: number;

  context: CanvasRenderingContext2D | null = null;


  constructor(
    private el: ElementRef,
  ) { }

  ngOnInit(): void {
    // console.log('ngOnInit()', this.wavCanvas === null);
    this.maxDiff = this.recorder?.maxDiff ?? MAX_DIFF;
    this.lineDiff = this.recorder?.lineDiff ?? INTONATION_STEP;
    console.log('ngOnInit()', this);

    this.offsetWidth = this.el.nativeElement.clientWidth;
    this.offsetHeight = this.el.nativeElement.clientHeight;
  }

  ngAfterViewInit(): void {
    // console.log('ngAfterViewInit()');
    if (this.wavCanvas) {
      this.context = (this.wavCanvas.nativeElement as HTMLCanvasElement).getContext('2d');

      timer(0).subscribe(() => {
        this.calcCanvasVars();
        this.draw();
      });
    }
    else {
      throw new Error('WavGraphComponent::ngAfterViewInit() wavCanvas is not defined');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const changedPropNames = Object.keys(changes);
    // console.warn('ngOnChanges()', changes, changedPropNames);
    if (changedPropNames.includes('data') && this.offsetHeight !== undefined) {
      console.log('ngOnChanges() data changed');
      this.calcCanvasVars();
      this.draw();
    }
    // this.calcCanvasVars();
    // this.draw();
  }

  private draw(): void {
    // console.log('WavGraphComponent::draw()', this.data);

    if (this.context && this.offsetWidth !== undefined && this.offsetHeight !== undefined) {
      this.context.clearRect(0, 0, this.offsetWidth, this.offsetHeight);
    }

    if (this.showLine && this.context) {
      if (this.offsetWidth === undefined || this.offsetHeight === undefined || this.lineDiff === undefined) {
        throw new Error('WavGraphComponent::draw() offsetWidth, offsetHeight, lineDiff is not defined');
      }
      this.context.strokeStyle = 'rgb(251, 192, 45)';
      this.context.lineWidth = 2;
      this.context.setLineDash([5, 5]);

      const y1 = this.offsetHeight * (RATIO_PADDING_Y);
      const y3 = this.offsetHeight * (1 - RATIO_PADDING_Y);
      this.context.beginPath();
      this.context.moveTo(this.paddingX, this.offsetHeight / 2);
      this.context.lineTo(this.offsetWidth - this.paddingX, this.offsetHeight / 2);
      this.context.moveTo(this.paddingX, y1);
      this.context.lineTo(this.offsetWidth - this.paddingX, y1);
      this.context.moveTo(this.paddingX, y3);
      this.context.lineTo(this.offsetWidth - this.paddingX, y3);
      this.context.stroke();

    }

    if (this.data && this.data.length > 0 && this.context) {
      if (this.offsetWidth === undefined || this.offsetHeight === undefined || this.lineDiff === undefined) {
        throw new Error('WavGraphComponent::draw() offsetWidth, offsetHeight, lineDiff is not defined');
      }
      if (this.recorder === undefined) {
        throw new Error('WavGraphComponent::draw() recorder is not defined');
      }
      const len = this.data.length;
      let state = STATE.INIT;
      let countNaN: number = 0;

      this.context.strokeStyle = 'rgb(255, 111, 0)';
      this.context.lineWidth = 3;
      this.context.setLineDash([]);

      let points: Array<number> = [];

      for (let i = 0; i < len; ++i) {
        const item = this.data[i];
        // console.log(i, item, flag);

        // console.log('WavGraphComponent::draw() state = ', state, item, i);
        switch (state) {
          case STATE.INIT:
            if (!isNaN(item)) {
              // console.log('STATE.INIT::1');
              points.push(i, item);
              state = STATE.NOT_NAN;
            }
            // else {
            //   console.log('STATE.INIT::2');
            // }
            break;
          case STATE.CHECK_NAN:
            if (isNaN(item)) {
              ++countNaN;
              if (countNaN > MAX_NAN_DRAW_LINE) {
                // console.warn('STATE.CHECK_NAN::1');
                points[points.length - 2] += MAKE_NARROW; // 직전 X 값에 +n을 한다. 간격을 좁히기 위해서
                this.drawCurve(points);
                points = [];
                state = STATE.EMPTY_NAN;
              }
              // else {
              //   console.log('STATE.CHECK_NAN::2');
              // }
            }
            else {
              // console.log('STATE.CHECK_NAN::3');
              points.push(i, item);
              state = STATE.NOT_NAN;
            }
            break;
          case STATE.EMPTY_NAN:
            if (!isNaN(item)) {
              // console.log('STATE.EMPTY_NAN::1');
              points.push(i - MAKE_NARROW, item); // -n 한 이유는, 간격을 좁히기위해서
              state = STATE.NOT_NAN;
            }
            // else {
            //   console.log('STATE.EMPTY_NAN::2');
            // }
            break;
          case STATE.NOT_NAN:
            if (!isNaN(item)) {
              // console.log('STATE.NOT_NAN::1');
              points.push(i, item);
            }
            else {
              // console.log('STATE.NOT_NAN::2');
              state = STATE.CHECK_NAN;
              countNaN = 1;
            }
            break;
        }
      }
      if (state === STATE.NOT_NAN || state === STATE.CHECK_NAN) {
        // console.warn('STATE.NOT_NAN::3 - last');
        this.drawCurve(points);
        // this.context.stroke();
      }
    }
    else {
      // TODO: 그래프 없음을 표시해야 함.
      console.log('print no data!');
    }
  }

  private calcCanvasVars(): void {
    // console.log('WavGraphComponent::calcCanvasVars()');

    if (this.data && this.data.length > 0) {
      if (this.offsetWidth === undefined || this.offsetHeight === undefined || this.maxDiff === undefined) {
        console.error('WavGraphComponent::calcCanvasVars() offsetWidth, offsetHeight, maxDiff is not defined', {...this});
        throw new Error('WavGraphComponent::calcCanvasVars() offsetWidth, offsetHeight, maxDiff is not defined');
      }
      if (this.recorder === undefined) {
        throw new Error('WavGraphComponent::calcCanvasVars() recorder is not defined');
      }
      const len = this.data.length - 1;

      // let min = 1.0;
      // let max = -1.0;
      // this.data.forEach(value => {
      //   if (!isNaN(value)) {
      //     min = value < min ? value : min;
      //     max = value > max ? value : max;
      //   }
      // });
      // this.avg = (min + max) / 2.0;
      // console.log(min, max, avg);

      this.startX = this.paddingX;
      this.ratioX = (this.offsetWidth - 2.0 * this.paddingX) / len;

      this.ratioY = (2 * this.paddingY - this.offsetHeight) / this.maxDiff;
      this.startY = this.offsetHeight / 2.0 - (2.0 * this.paddingY - this.offsetHeight) * this.recorder.voiceAvg / this.maxDiff;

      // console.log('calcCanvasVars()', this);
    }
  }

  private getX(x: number): number {
    if (this.startX === undefined || this.ratioX === undefined) {
      throw new Error('WavGraphComponent::getX() this.startX or this.ratioX is undefined');
    }
    return this.startX + this.ratioX * x;
  }

  private getY(y: number): number {
    if (this.startY === undefined || this.ratioY === undefined) {
      throw new Error('WavGraphComponent::getY() this.startY or this.ratioY is undefined');
    }

    // 중앙(고저 3번 = this.recorder.voiceAvg)을 기준으로 위쪽은 RATIO 배욜로 너 넓게 표시한다.
    const RATIO = 1.5;  // 1보다 큰 값으로 설정
    if (this.recorder && this.recorder.voiceAvg !== undefined) {
      const shift = this.recorder.voiceAvg - this.recorder.voiceAvg / RATIO;
      if (y >= this.recorder.voiceAvg) {
        return this.startY + this.ratioY * RATIO * (y - shift);
      }
    }

    return this.startY + this.ratioY * y;
  }

  private drawCurve(points: Array<number>): void {
    // console.log('drawCurve()', points);

    if (this.context === null) {
      throw new Error('WavGraphComponent::drawCurve() this.context is null');
    }
    const line = points.map((p, index) => index % 2 ? this.getY(p) : this.getX(p));
    drawCurve(this.context, line);
  }
}
